/**
 * Debug logging - channel-gated, silent by default.
 *
 * Shared by the browser game and the headless simulator. Both used to
 * console.log unconditionally from the same -core.js modules, which meant the
 * game spammed the devtools console and a 90-day simulator run produced a
 * ~169MB log that had to be grepped to find anything.
 *
 * Nothing here prints unless a channel is explicitly enabled:
 *
 *   Browser:   localStorage.setItem('debug', 'raid,events')
 *   Node:      DEBUG=raid,events node tools/simulation/game-simulator.js
 *   Either:    setDebugChannels(['raid'])   // programmatic
 *
 * '*' enables everything. Channel names are the CHANNELS keys below.
 *
 * Use isDebug() to guard any message whose ARGUMENTS are expensive to build -
 * debug() itself is cheap when disabled, but its arguments are still evaluated
 * at the call site:
 *
 *   if (isDebug(CH.loot)) debug(CH.loot, `pool: ${buildExpensiveSummary()}`);
 */

/** Channel names. Use these constants rather than bare strings. */
export const CH = {
  raid: 'raid',       // raid lifecycle: start/validate/process/end, room timing
  events: 'events',   // event scheduling, selection, cleanup
  loot: 'loot',       // loot pools, drop rolls, rate multipliers
  craft: 'craft',     // crafting decisions and material consumption
  path: 'path',       // pathfinding and target selection
  sim: 'sim',         // simulator-only progress chatter
  ui: 'ui',           // browser-only: rendering, save/load, character switching
};

const ALL_CHANNELS = Object.values(CH);

/** @type {Set<string>} */
let enabled = new Set();

function parseSpec(spec) {
  if (!spec) return [];
  return String(spec)
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

/**
 * Replace the set of enabled channels.
 * @param {string[]|string} channels - names, or '*' for all
 */
export function setDebugChannels(channels) {
  const list = Array.isArray(channels) ? channels : parseSpec(channels);
  enabled = list.includes('*') ? new Set(ALL_CHANNELS) : new Set(list);
}

/** @returns {string[]} currently enabled channel names */
export function getDebugChannels() {
  return [...enabled];
}

/**
 * Is this channel enabled? Guard expensive message construction with this.
 * @param {string} channel
 * @returns {boolean}
 */
export function isDebug(channel) {
  return enabled.has(channel);
}

/**
 * Log to a channel. No-op unless the channel is enabled.
 * @param {string} channel - one of CH.*
 * @param {...any} args - forwarded to console.log
 */
export function debug(channel, ...args) {
  if (enabled.has(channel)) console.log(`[${channel}]`, ...args);
}

// --- Auto-configure from the ambient environment -------------------------
// Wrapped in try/catch: `process` is undefined in the browser and
// `localStorage` throws in some sandboxed/SSR contexts.
try {
  if (typeof process !== 'undefined' && process.env && process.env.DEBUG) {
    setDebugChannels(process.env.DEBUG);
  } else if (typeof localStorage !== 'undefined') {
    const spec = localStorage.getItem('debug');
    if (spec) setDebugChannels(spec);
  }
} catch {
  /* leave all channels disabled */
}
